import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Users, Activity, AlertCircle, FileBarChart, BarChart3, Brain } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  Cell,
  ScatterChart,
  Scatter
} from 'recharts';

export const IVFCancellationRiskCaseStudy = () => {
  const navigate = useNavigate();

  // Table 1: Analytical Pipeline
  const analyticalPipeline = [
    {
      stage: "1",
      method: "Logistic Regression",
      question: "Which clinical factors independently predict cancellation? By what odds?",
      rPackage: "stats, broom, caret"
    },
    {
      stage: "2",
      method: "Random Forest",
      question: "Can non-linear interactions between AMH, AFC, FSH, and Age improve prediction?",
      rPackage: "randomForest, caret"
    },
    {
      stage: "3",
      method: "Gradient Boosting (GBM)",
      question: "Does sequential error-correction improve performance over independent trees?",
      rPackage: "gbm, caret"
    },
    {
      stage: "4",
      method: "SHAP Interpretation",
      question: "For each patient, why does the model predict high/low risk? What drives the prediction?",
      rPackage: "iml, shapviz"
    },
    {
      stage: "5",
      method: "Calibration Analysis",
      question: "Are predicted probabilities trustworthy? Does 70% predicted risk reflect ~70% actual cancellation?",
      rPackage: "CalibrationCurves, ResourceSelection"
    },
    {
      stage: "6",
      method: "External Validation",
      question: "Does the model generalise to a new, independent patient cohort?",
      rPackage: "pROC"
    },
    {
      stage: "7",
      method: "Sensitivity Analysis",
      question: "Are results robust to SMOTE ratio, and which features matter most?",
      rPackage: "caret, pROC"
    }
  ];

  // Table 2: Dataset Structure
  const datasetStructure = [
    {
      dataset: "df (Training)",
      n: "500",
      description: "Primary dataset. 80/20 split into train_smote (post-SMOTE) and test_data. Source for model training."
    },
    {
      dataset: "df_ext (External)",
      n: "200",
      description: "External validation cohort. Independently generated with slightly different population parameters. Simulates a different clinic."
    }
  ];

  // Table 3: Variable Dictionary
  const variableDictionary = [
    {
      variable: "age",
      type: "Continuous",
      description: "Age at treatment (years, 22–45). Older age correlates with declining ovarian reserve."
    },
    {
      variable: "bmi",
      type: "Continuous",
      description: "Body Mass Index. Extremes affect hormonal response and follicular development."
    },
    {
      variable: "amh",
      type: "Continuous",
      description: "Anti-Müllerian Hormone (ng/mL). Gold-standard ovarian reserve marker. Low AMH = high risk."
    },
    {
      variable: "afc",
      type: "Count",
      description: "Antral Follicle Count. Ultrasound measure. Direct proxy of expected stimulation response."
    },
    {
      variable: "fsh",
      type: "Continuous",
      description: "Follicle Stimulating Hormone (IU/L). Elevated FSH indicates declining ovarian function."
    },
    {
      variable: "lh",
      type: "Continuous",
      description: "Luteinising Hormone (IU/L). Elevated LH may indicate PCOS or poor reserve."
    },
    {
      variable: "estradiol",
      type: "Continuous",
      description: "Baseline serum oestradiol (pmol/L). Elevated baseline E2 may suppress FSH."
    },
    {
      variable: "prev_cycles",
      type: "Count",
      description: "Number of prior IVF cycles. Prior cancellations indicate poor response pattern."
    },
    {
      variable: "protocol",
      type: "Categorical",
      description: "Stimulation protocol: Long / Short / Antagonist."
    },
    {
      variable: "cause",
      type: "Categorical",
      description: "Primary infertility diagnosis: Tubal / Male / Unexplained / Endometriosis / PCOS / Combined."
    },
    {
      variable: "cancelled",
      type: "Binary",
      description: "Outcome: Yes = cycle stopped before retrieval. No = proceeded to retrieval."
    }
  ];

  // Model Performance Comparison - Table 7
  const modelPerformance = [
    {
      model: "Logistic Regression",
      auc: 0.710,
      accuracy: 0.657,
      sensitivity: 0.679,
      specificity: 0.533,
      interpretation: "Baseline benchmark, fully interpretable"
    },
    {
      model: "Random Forest ✓",
      auc: 0.729,
      accuracy: 0.747,
      sensitivity: 0.821,
      specificity: 0.333,
      interpretation: "Best performer, highest sensitivity"
    },
    {
      model: "GBM",
      auc: 0.679,
      accuracy: 0.646,
      sensitivity: 0.667,
      specificity: 0.533,
      interpretation: "Underperformed on small dataset"
    }
  ];

  // ROC Curve Data (simulated based on AUC values)
  const generateROCData = (targetAUC) => {
    const points = [];
    points.push({ fpr: 0, tpr: 0 });
    for (let i = 1; i <= 19; i++) {
      const fpr = i / 20;
      const exponent = 1 / (2 * targetAUC);
      let tpr = Math.pow(fpr, exponent);
      tpr += (Math.random() - 0.5) * 0.03;
      tpr = Math.max(0, Math.min(1, tpr));
      points.push({ fpr, tpr });
    }
    points.push({ fpr: 1, tpr: 1 });
    return points;
  };

  const rocLR = generateROCData(0.710);
  const rocRF = generateROCData(0.729);
  const rocGBM = generateROCData(0.679);
  const rocExternal = generateROCData(0.711);

  // SHAP Feature Importance
  const shapImportance = [
    { feature: "AMH", importance: 100, color: "#dc2626" },
    { feature: "Age", importance: 89, color: "#ea580c" },
    { feature: "AFC", importance: 86, color: "#f59e0b" },
    { feature: "BMI", importance: 46, color: "#84cc16" },
    { feature: "FSH", importance: 12, color: "#22c55e" },
    { feature: "prev_cycles", importance: 8, color: "#10b981" }
  ];

  // Risk Stratification
  const riskStratification = [
    {
      tier: "Low Risk",
      probability: "< 30%",
      percentage: 38.4,
      expectedRate: "~15%",
      action: "Standard protocol. Routine monitoring at days 6 and 9.",
      color: "bg-green-100 text-green-800 border-green-300"
    },
    {
      tier: "Medium Risk",
      probability: "30% – 70%",
      percentage: 51.5,
      expectedRate: "~48%",
      action: "Enhanced monitoring (days 5, 7, 9). Consider modified protocol. Patient counselling.",
      color: "bg-yellow-100 text-yellow-800 border-yellow-300"
    },
    {
      tier: "High Risk",
      probability: "> 70%",
      percentage: 10.1,
      expectedRate: "~82%",
      action: "Protocol modification strongly recommended. Comprehensive counselling. Discuss alternatives.",
      color: "bg-red-100 text-red-800 border-red-300"
    }
  ];

  // Patient Profiles by Risk Tier
  const patientProfiles = [
    { tier: "Low", meanAge: 31.2, meanAMH: 3.8, meanAFC: 14.2, meanFSH: 6.1 },
    { tier: "Medium", meanAge: 34.8, meanAMH: 1.9, meanAFC: 8.7, meanFSH: 8.4 },
    { tier: "High", meanAge: 39.1, meanAMH: 0.6, meanAFC: 4.1, meanFSH: 12.8 }
  ];

  // Calibration data (observed vs predicted)
  const calibrationData = [
    { predicted: 0.1, observed_LR: 0.09, observed_RF: 0.12, observed_GBM: 0.08 },
    { predicted: 0.2, observed_LR: 0.19, observed_RF: 0.24, observed_GBM: 0.18 },
    { predicted: 0.3, observed_LR: 0.31, observed_RF: 0.36, observed_GBM: 0.28 },
    { predicted: 0.4, observed_LR: 0.42, observed_RF: 0.48, observed_GBM: 0.39 },
    { predicted: 0.5, observed_LR: 0.51, observed_RF: 0.58, observed_GBM: 0.48 },
    { predicted: 0.6, observed_LR: 0.61, observed_RF: 0.67, observed_GBM: 0.59 },
    { predicted: 0.7, observed_LR: 0.72, observed_RF: 0.76, observed_GBM: 0.69 },
    { predicted: 0.8, observed_LR: 0.81, observed_RF: 0.84, observed_GBM: 0.79 },
    { predicted: 0.9, observed_LR: 0.89, observed_RF: 0.91, observed_GBM: 0.87 }
  ];

  // External Validation Comparison
  const externalValidation = [
    { dataset: "Internal Test", auc: 0.729, accuracy: 0.747, sensitivity: 0.821, specificity: 0.333 },
    { dataset: "External Validation", auc: 0.711, accuracy: 0.720, sensitivity: 0.796, specificity: 0.358 }
  ];

  // Feature Ablation Results
  const featureAblation = [
    { variable: "Full Model", auc: 0.729, drop: 0 },
    { variable: "Without AMH", auc: 0.634, drop: 0.095 },
    { variable: "Without AFC", auc: 0.692, drop: 0.037 },
    { variable: "Without Age", auc: 0.701, drop: 0.028 },
    { variable: "Without FSH", auc: 0.723, drop: 0.006 },
    { variable: "Without BMI", auc: 0.716, drop: 0.013 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 pt-16 md:pt-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => navigate('/case-studies')}
            className="flex items-center gap-2 text-purple-100 hover:text-white mb-6 transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Case Studies
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Badge className="mb-4 bg-cyan-500 hover:bg-cyan-600 text-white border-0">
                Predictive Modeling & Clinical Decision Support
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
                IVF Cycle Cancellation Risk Prediction Using Machine Learning
              </h1>
              <p className="text-lg sm:text-xl text-purple-100 mb-6 leading-relaxed max-w-4xl">
                Logistic Regression · Random Forest · GBM · SHAP · Calibration · External Validation
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 bg-purple-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <Users className="h-4 w-4" />
                  <span>500 training + 200 validation</span>
                </div>
                <div className="flex items-center gap-2 bg-purple-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <BarChart3 className="h-4 w-4" />
                  <span>3 ML Models</span>
                </div>
                <div className="flex items-center gap-2 bg-purple-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <Brain className="h-4 w-4" />
                  <span>SHAP Explainability</span>
                </div>
              </div>
            </div>
          </div>

          {/* Simulated Data Disclaimer */}
          <div className="mt-6 bg-yellow-500/20 border border-yellow-300/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-200 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-50 font-medium">Simulated Data for Demonstration</p>
                <p className="text-xs text-yellow-100 mt-1">
                  All datasets, model outputs, and visualizations use synthetically generated data (set.seed(2026)) for portfolio purposes. Results demonstrate advanced ML methodology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Part 2 Navigation Card */}
        <Card className="mb-8 border-2 border-teal-400 bg-gradient-to-r from-teal-50 to-blue-50">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">This Is Part 2 of 2</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This model was built on the same clinic group featured in Part 1. The dashboard identified where cancellations were happening. This model predicts who will cancel. before the cycle begins.
            </p>
            <button
              onClick={() => navigate('/case-studies/ivf-performance-dashboard')}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors group"
            >
              <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
              Back to Part 1: The Performance Dashboard
            </button>
          </CardContent>
        </Card>

        {/* At a Glance */}
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">At a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-purple-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Situation</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The dashboard showed where cancellations were happening. but the clinic needed a way to identify which patients were heading toward cancellation before treatment began.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What We Built</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A machine learning cancellation risk model built entirely on variables collected at first consultation. no additional testing required. Three models tested, Random Forest selected.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Model Performance</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  AUC 0.729 internal, 0.711 external validation. Sensitivity 82.1%. meaning the model detected 82% of at-risk cycles before treatment began. AMH emerged as the single strongest predictor.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Problem Statement */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur border-l-4 border-purple-600">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Business and Clinical Problem</h2>
            <div className="space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>IVF cycle cancellation</strong> occurs when ovarian stimulation results in a poor response (too few follicles) 
                or excessive response (hyperstimulation risk), forcing the cycle to be stopped before egg retrieval. This is clinically 
                and emotionally distressing for patients, financially costly, and represents a missed treatment opportunity.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Early identification of high-risk patients</strong> from routine pre-treatment clinical characteristics enables:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span><strong>Personalized stimulation protocols:</strong> Adjust medication dosage and regimen based on predicted risk</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span><strong>Proactive patient counselling:</strong> Set realistic expectations and discuss alternative approaches</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span><strong>Resource optimization:</strong> Reduce avoidable cancellations and improve clinic efficiency</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span><strong>Clinical decision support:</strong> Evidence-based risk stratification at point of consultation</span>
                </li>
              </ul>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                <p className="text-gray-700 leading-relaxed">
                  This case study develops, validates, and interprets a suite of <strong>three machine learning models</strong> 
                  (Logistic Regression, Random Forest, Gradient Boosting) with emphasis on <strong>SHAP explainability</strong>, 
                  <strong>calibration assessment</strong>, and <strong>external validation</strong> on 200 independent patients.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Executive Summary */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl">
                <FileBarChart className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Executive Summary</h2>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>Objective:</strong> Develop, validate, and interpret machine learning models to predict IVF cycle 
                cancellation risk from pre-treatment clinical characteristics.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Methodology</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Synthetic dataset: 500 training + 200 external validation</li>
                    <li>• Three models: Logistic Regression, Random Forest, GBM</li>
                    <li>• 5-fold cross-validation with SMOTE for class imbalance</li>
                    <li>• SHAP for patient-level explanations</li>
                    <li>• Calibration curves, Hosmer-Lemeshow, Brier scores</li>
                    <li>• External validation on independent cohort</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Key Outcome</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Random Forest</strong> selected as optimal model:
                  </p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>AUC:</strong> 0.729 (internal), 0.711 (external)</li>
                    <li>• <strong>Sensitivity:</strong> 0.821 (detects 82% of cancellations)</li>
                    <li>• <strong>Top Predictor:</strong> AMH (SHAP importance: 100)</li>
                    <li>• Minimal performance drop confirms generalizability</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table 1: Analytical Pipeline */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Table 1: Seven-Stage Analytical Pipeline</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Stage</th>
                    <th className="text-left py-3 px-4 font-semibold">Method</th>
                    <th className="text-left py-3 px-4 font-semibold">Clinical Question Answered</th>
                    <th className="text-left py-3 px-4 font-semibold">R Package</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {analyticalPipeline.map((stage, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-purple-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-purple-700">{stage.stage}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{stage.method}</td>
                      <td className="py-3 px-4 text-gray-700">{stage.question}</td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-600">{stage.rPackage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Study Design & Data */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Study Design & Data</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Source</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Synthetic dataset simulating real-world IVF clinic patient registry</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Variables reflect standard first fertility consultation measurements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Two independent cohorts for external validation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Reproducible with <code className="bg-white px-2 py-1 rounded text-sm font-mono">set.seed(2026)</code></span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Table 2: Dataset Structure */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Table 2: Dataset Structure</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Dataset</th>
                        <th className="text-left py-3 px-4 font-semibold">n</th>
                        <th className="text-left py-3 px-4 font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {datasetStructure.map((ds, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-medium text-gray-900">{ds.dataset}</td>
                          <td className="py-3 px-4 font-mono text-purple-700 font-semibold">{ds.n}</td>
                          <td className="py-3 px-4 text-gray-700">{ds.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Table 3: Variable Dictionary */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Table 3: Complete Variable Dictionary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Variable</th>
                        <th className="text-left py-3 px-4 font-semibold">Type</th>
                        <th className="text-left py-3 px-4 font-semibold">Description & Clinical Relevance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {variableDictionary.map((variable, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0 hover:bg-blue-50 transition-colors">
                          <td className="py-3 px-4 font-mono text-xs text-blue-700 font-medium">{variable.variable}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-xs">{variable.type}</Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-700">{variable.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">Class Imbalance Handling</h4>
                <p className="text-sm text-gray-700">
                  IVF cancellation is a minority outcome (15–25%). SMOTE (Synthetic Minority Oversampling Technique) 
                  is applied exclusively to the training set to address this. <strong>Critical Rule:</strong> SMOTE 
                  must never be applied to test or external validation sets to prevent data leakage and inflated metrics.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Model Performance Comparison */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Model Performance Comparison</h2>
            
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Model</th>
                    <th className="text-center py-3 px-4 font-semibold">AUC</th>
                    <th className="text-center py-3 px-4 font-semibold">Accuracy</th>
                    <th className="text-center py-3 px-4 font-semibold">Sensitivity</th>
                    <th className="text-center py-3 px-4 font-semibold">Specificity</th>
                    <th className="text-left py-3 px-4 font-semibold">Interpretation</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {modelPerformance.map((model, idx) => (
                    <tr key={idx} className={`border-b border-gray-200 last:border-0 ${model.model.includes('✓') ? 'bg-green-50 font-semibold' : 'hover:bg-gray-50'}`}>
                      <td className="py-3 px-4 text-gray-900">{model.model}</td>
                      <td className="text-center py-3 px-4 font-mono text-blue-700">{model.auc.toFixed(3)}</td>
                      <td className="text-center py-3 px-4 text-gray-700">{model.accuracy.toFixed(3)}</td>
                      <td className="text-center py-3 px-4 text-green-700 font-semibold">{model.sensitivity.toFixed(3)}</td>
                      <td className="text-center py-3 px-4 text-gray-700">{model.specificity.toFixed(3)}</td>
                      <td className="py-3 px-4 text-gray-700 text-xs">{model.interpretation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Winner: Random Forest</strong> selected for deployment. Highest AUC (0.729) and sensitivity (0.821 = detects 82.1% of actual cancellations). 
                Low specificity (0.333) generates false alarms but is clinically preferable to missing high-risk patients.
              </p>
            </div>

            {/* ROC Curves */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 1: ROC Curves - Model Comparison</h3>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="fpr"
                      domain={[0, 1]}
                      ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                      label={{ value: 'False Positive Rate (1 - Specificity)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis 
                      domain={[0, 1]}
                      ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                      label={{ value: 'True Positive Rate (Sensitivity)', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip 
                      formatter={(value) => value.toFixed(3)}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    <ReferenceLine 
                      segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} 
                      stroke="#94a3b8" 
                      strokeWidth={2} 
                      strokeDasharray="5 5"
                      label={{ value: 'Random Classifier', position: 'center', fill: '#64748b', fontSize: 11 }}
                    />
                    <Line 
                      data={rocLR}
                      type="monotone" 
                      dataKey="tpr" 
                      stroke="#3b82f6" 
                      strokeWidth={2.5}
                      name="Logistic Regression (AUC=0.710)"
                      dot={false}
                    />
                    <Line 
                      data={rocRF}
                      type="monotone" 
                      dataKey="tpr" 
                      stroke="#059669" 
                      strokeWidth={3}
                      name="Random Forest (AUC=0.729)"
                      dot={false}
                    />
                    <Line 
                      data={rocGBM}
                      type="monotone" 
                      dataKey="tpr" 
                      stroke="#dc2626" 
                      strokeWidth={2.5}
                      name="GBM (AUC=0.679)"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-sm text-gray-600 mt-4 text-center">
                  Random Forest (green) achieves highest AUC, indicating best overall discriminative ability.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SHAP Interpretation */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SHAP-Based Model Interpretation</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Why SHAP?</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Standard variable importance tells us <em>"AMH is the most important variable overall"</em>. 
                  <strong> SHAP values</strong> provide patient-level explanations: <em>"For Patient X, their AMH of 0.4 ng/mL 
                  increased their predicted risk by +0.22 probability points."</em> This enables clinician-facing communication 
                  and builds trust in model predictions.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 2: Global SHAP Feature Importance</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={shapImportance} 
                      layout="vertical"
                      margin={{ top: 10, right: 30, bottom: 20, left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        type="number" 
                        domain={[0, 100]}
                        label={{ value: 'SHAP Importance Score', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="feature" 
                        tick={{ fontSize: 12 }}
                        width={90}
                      />
                      <Tooltip 
                        formatter={(value) => value.toFixed(1)}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Bar dataKey="importance" radius={[0, 4, 4, 0]}>
                        {shapImportance.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    AMH dominates as the strongest predictor, followed by Age and AFC. FSH contributes minimally (information captured by AMH/AFC).
                  </p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Clinical Application</h4>
                <p className="text-sm text-gray-700">
                  For a patient flagged as high-risk (predicted probability 78%), SHAP breakdown might show: 
                  <em>"AMH=0.4 ng/mL contributed +0.22, Age=41 contributed +0.09, AFC=3 contributed +0.15."</em> 
                  This enables personalized conversations: <em>"Your low AMH is the primary driver of elevated risk."</em>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calibration Analysis */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Calibration Analysis</h2>

            <div className="space-y-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Why Calibration Matters</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  A model with high AUC may still have systematically wrong probability estimates. Poor calibration is dangerous: 
                  predicting 70% risk when actual is 35% leads to over-treatment; predicting 30% when actual is 60% causes dangerous 
                  under-treatment. Calibration ensures predicted probabilities are trustworthy.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 3: Calibration Curves</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={calibrationData} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="predicted"
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'Predicted Probability', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'Observed Cancellation Rate', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        formatter={(value) => (value * 100).toFixed(1) + '%'}
                        labelFormatter={(label) => `Predicted: ${(label * 100).toFixed(0)}%`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <ReferenceLine 
                        segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} 
                        stroke="#64748b" 
                        strokeWidth={2} 
                        strokeDasharray="5 5"
                        label={{ value: 'Perfect Calibration (45° line)', position: 'center', fill: '#64748b', fontSize: 11 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="observed_LR" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        name="Logistic Regression"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="observed_RF" 
                        stroke="#059669" 
                        strokeWidth={3}
                        name="Random Forest"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="observed_GBM" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        name="GBM"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Logistic Regression (blue) shows best calibration. Random Forest slightly over-predicts. Points near the diagonal indicate good calibration.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Calibration Curve</h4>
                  <p className="text-xs text-gray-700">Plots observed vs predicted in decile bins. Perfect = 45° diagonal.</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Hosmer-Lemeshow</h4>
                  <p className="text-xs text-gray-700">Chi-squared test. p &gt; 0.05 = acceptable calibration.</p>
                </div>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Brier Score</h4>
                  <p className="text-xs text-gray-700">Mean squared error. 0 = perfect; 0.25 = uninformative.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* External Validation */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">External Validation</h2>

            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Purpose & Importance</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  External validation tests if a model trained on one population (df, n=500) generalizes to a different, 
                  independent population (df_ext, n=200). This simulates real-world deployment in a different clinic 
                  with slightly different patient characteristics. A model that degrades externally is overfit and unsuitable 
                  for deployment.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 11: Internal vs External Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Dataset</th>
                        <th className="text-center py-3 px-4 font-semibold">AUC</th>
                        <th className="text-center py-3 px-4 font-semibold">Accuracy</th>
                        <th className="text-center py-3 px-4 font-semibold">Sensitivity</th>
                        <th className="text-center py-3 px-4 font-semibold">Specificity</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {externalValidation.map((val, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-medium text-gray-900">{val.dataset}</td>
                          <td className="text-center py-3 px-4 font-mono text-blue-700 font-semibold">{val.auc.toFixed(3)}</td>
                          <td className="text-center py-3 px-4 text-gray-700">{val.accuracy.toFixed(3)}</td>
                          <td className="text-center py-3 px-4 text-green-700">{val.sensitivity.toFixed(3)}</td>
                          <td className="text-center py-3 px-4 text-gray-700">{val.specificity.toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">✓ Excellent Retention</h4>
                  <p className="text-sm text-gray-700">
                    AUC drop of only 0.018 (0.729 → 0.711) is minimal and well within acceptable range. 
                    This confirms the model generalizes successfully to independent patient cohorts.
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Bootstrap 95% CI</h4>
                  <p className="text-sm text-gray-700">
                    External AUC 95% CI: [0.642, 0.779] (n=1000 bootstrap resamples). Confirms robustness.
                  </p>
                </div>
              </div>

              {/* External Validation ROC */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 4: External Validation ROC Curve</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="fpr"
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'False Positive Rate', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'True Positive Rate', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        formatter={(value) => value.toFixed(3)}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <ReferenceLine 
                        segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} 
                        stroke="#94a3b8" 
                        strokeWidth={2} 
                        strokeDasharray="5 5"
                      />
                      <Line 
                        data={rocExternal}
                        type="monotone" 
                        dataKey="tpr" 
                        stroke="#7c3aed" 
                        strokeWidth={3}
                        name="Random Forest - External (AUC=0.711)"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    External validation on 200 independent patients confirms model generalizability.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Stratification */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Risk Stratification Framework</h2>

            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Predicted probabilities are translated into three clinical risk tiers with management recommendations. 
                Thresholds defined at 30% and 70% based on clinical consensus and Youden index optimization.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {riskStratification.map((tier, idx) => (
                  <div key={idx} className={`rounded-lg p-6 border-2 ${tier.color}`}>
                    <h3 className="text-xl font-bold mb-2">{tier.tier}</h3>
                    <p className="text-sm mb-3">
                      <strong>Probability:</strong> {tier.probability}
                    </p>
                    <p className="text-sm mb-3">
                      <strong>Prevalence:</strong> {tier.percentage}% of cohort
                    </p>
                    <p className="text-sm mb-3">
                      <strong>Observed Rate:</strong> {tier.expectedRate}
                    </p>
                    <div className="bg-white rounded p-3 mt-3">
                      <p className="text-xs font-semibold text-gray-900 mb-1">Clinical Action:</p>
                      <p className="text-xs text-gray-700">{tier.action}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Patient Profiles by Risk Tier */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 13: Patient Profiles by Risk Tier</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Risk Tier</th>
                        <th className="text-center py-3 px-4 font-semibold">Mean Age</th>
                        <th className="text-center py-3 px-4 font-semibold">Mean AMH</th>
                        <th className="text-center py-3 px-4 font-semibold">Mean AFC</th>
                        <th className="text-center py-3 px-4 font-semibold">Mean FSH</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {patientProfiles.map((profile, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-medium text-gray-900">{profile.tier}</td>
                          <td className="text-center py-3 px-4 text-gray-700">{profile.meanAge.toFixed(1)}</td>
                          <td className="text-center py-3 px-4 font-semibold text-red-700">{profile.meanAMH.toFixed(1)}</td>
                          <td className="text-center py-3 px-4 text-gray-700">{profile.meanAFC.toFixed(1)}</td>
                          <td className="text-center py-3 px-4 text-gray-700">{profile.meanFSH.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  High-risk patients are older with dramatically lower AMH/AFC and elevated FSH, confirming biological face-validity.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AMH Callout */}
        <Card className="mb-8 border-2 border-teal-400 bg-teal-50">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">The Most Important Variable: AMH</h3>
            <p className="text-gray-700 leading-relaxed">
              AMH (Anti-Müllerian Hormone) was the single strongest predictor of cancellation risk across all three models. Removing AMH from the model caused the largest performance drop of any variable. an AUC decrease of 0.095 (from 0.729 to 0.634). This confirms AMH is not an optional data point at first consultation. It is the foundation of individual risk assessment. Clinics not routinely collecting AMH before stimulation are making protocol decisions with their most important predictive variable missing.
            </p>
          </CardContent>
        </Card>

        {/* Feature Ablation */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Sensitivity Analysis: Feature Ablation</h2>

            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Feature ablation tests model performance when individual variables are removed. Large AUC drops indicate 
                irreplaceable variables. This analysis confirms AMH's critical importance and FSH's redundancy.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold">Variable Configuration</th>
                      <th className="text-center py-3 px-4 font-semibold">AUC</th>
                      <th className="text-center py-3 px-4 font-semibold">AUC Drop</th>
                      <th className="text-left py-3 px-4 font-semibold">Interpretation</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {featureAblation.map((row, idx) => (
                      <tr key={idx} className={`border-b border-gray-200 last:border-0 ${idx === 0 ? 'bg-green-50 font-semibold' : idx === 1 ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                        <td className="py-3 px-4 text-gray-900">{row.variable}</td>
                        <td className="text-center py-3 px-4 font-mono text-blue-700">{row.auc.toFixed(3)}</td>
                        <td className="text-center py-3 px-4 font-semibold" style={{ color: row.drop > 0.05 ? '#dc2626' : row.drop > 0.02 ? '#f59e0b' : '#059669' }}>
                          {row.drop === 0 ? '—' : `-${row.drop.toFixed(3)}`}
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-xs">
                          {idx === 0 && "Baseline model with all features"}
                          {idx === 1 && "⚠️ Largest drop - AMH is irreplaceable"}
                          {idx === 2 && "Moderate drop - AFC contributes independently"}
                          {idx === 3 && "Moderate drop - Age adds meaningful signal"}
                          {idx === 4 && "✓ Minimal drop - FSH info captured by AMH/AFC"}
                          {idx === 5 && "Small drop - BMI has modest contribution"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Key Finding:</strong> Removing AMH causes a 0.095 AUC drop (0.729 → 0.634), the largest decline 
                  of any feature. This confirms AMH's irreplaceability and biological centrality to ovarian reserve assessment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limitations */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Limitations</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Data Limitations</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Synthetic data: Simulates but doesn't reproduce real patient heterogeneity</li>
                  <li>• Missing variables: Endometrial thickness, sperm parameters, day-3 follicle sizes</li>
                  <li>• Sample size: n=500 training; GBM typically requires n&gt;2,000</li>
                </ul>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Methodological</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Protocol confounding: Protocol choice based on expected response</li>
                  <li>• SMOTE assumptions: May not be ideal for count variables (AFC)</li>
                  <li>• RF specificity: Low (0.333) generates false alarms</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Validation</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• External cohort: Same time period, need prospective validation</li>
                  <li>• Real-world deployment: Requires actual registry data validation</li>
                  <li>• Temporal stability: Need to assess concept drift over time</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Clinical Acceptance</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Low specificity: Many false alarms may reduce clinician trust</li>
                  <li>• Calibration: RF may need Platt scaling before deployment</li>
                  <li>• Integration: Requires EHR integration for point-of-care use</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* R Package Reference */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">R Package Reference</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-900 text-white">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Package</th>
                    <th className="text-left py-3 px-4 font-semibold">Purpose</th>
                    <th className="text-left py-3 px-4 font-semibold">Key Functions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">caret</td>
                    <td className="py-3 px-4 text-gray-700">Unified ML framework</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">train(), confusionMatrix(), trainControl()</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">randomForest</td>
                    <td className="py-3 px-4 text-gray-700">Random Forest model</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">randomForest(), importance()</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">gbm</td>
                    <td className="py-3 px-4 text-gray-700">Gradient Boosting</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">gbm(), summary.gbm()</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">pROC</td>
                    <td className="py-3 px-4 text-gray-700">ROC & AUC</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">roc(), auc(), ci.auc(), coords()</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">DMwR2</td>
                    <td className="py-3 px-4 text-gray-700">SMOTE oversampling</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">SMOTE()</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">iml</td>
                    <td className="py-3 px-4 text-gray-700">SHAP & model explanation</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">Predictor$new(), FeatureImp$new()</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">CalibrationCurves</td>
                    <td className="py-3 px-4 text-gray-700">Calibration assessment</td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-600">val.prob()</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* What This Delivered */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Delivered</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">82% Detection Rate Before Treatment</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    The model detected 82.1% of patients who would go on to cancel. before their cycle even began. This shifts the clinical team from reactive (responding after cancellation) to proactive (adjusting protocols before stimulation).
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Patient-Level Risk Explanations</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    SHAP values transform a black-box prediction into a transparent clinical tool. When a patient is flagged as high-risk, the model shows exactly why. AMH contributed this much, age contributed this much, AFC contributed this much. This enables evidence-based patient conversations.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">AMH Confirmed as Critical Variable</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Feature ablation analysis confirmed AMH is irreplaceable. removing it caused the largest performance drop (AUC 0.729 → 0.634). Clinics not routinely collecting AMH at first consultation are making protocol decisions without their most important predictive variable.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Externally Validated on Independent Cohort</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    The model maintained AUC 0.711 on 200 patients not seen during training. demonstrating real-world generalizability. Models that perform well only on training data fail in clinical practice. External validation proves this model works on new patients.
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Services This Connects To */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Services Used in This Engagement</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">🔬</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Reproductive & IVF Analytics</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Cycle-level outcome analysis, KPI dashboard development, cancellation rate tracking by stage, protocol benchmarking, DHA/DOH/ESHRE regulatory reporting.
                  </p>
                  <button
                    onClick={() => navigate('/services#ivf-analytics')}
                    className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">📈</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Performance Monitoring & Dashboards</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Multi-center KPI dashboard, embryologist performance metrics, seasonal trend analysis, center-level comparison reporting, on-demand export.
                  </p>
                  <button
                    onClick={() => navigate('/services#performance-monitoring')}
                    className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">🤖</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Predictive Modelling & Advanced Analytics</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Machine learning cancellation risk model, SHAP explainability, three-tier risk stratification framework, external validation on independent cohort.
                  </p>
                  <button
                    onClick={() => navigate('/services#predictive-modelling')}
                    className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">📊</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Outcome Optimization</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Drop-out and cancellation root cause analysis by stage, protocol effectiveness evaluation by patient segment, embryo quality impact assessment.
                  </p>
                  <button
                    onClick={() => navigate('/services#outcome-optimization')}
                    className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 text-white">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold mb-4">Conclusion</h2>
            <p className="text-lg leading-relaxed text-purple-50 mb-4">
              This case study presents a complete, reproducible machine learning pipeline for IVF cycle cancellation 
              risk prediction. <strong>Random Forest</strong> emerged as the optimal model with external AUC of 0.711 
              and sensitivity of 0.821, demonstrating excellent generalizability with minimal performance drop.
            </p>
            <p className="text-lg leading-relaxed text-purple-50 mb-6">
              SHAP explainability addresses the "black box" concern, enabling clinician-facing communication. 
              AMH dominates as the strongest predictor, while GBM underperformance on this dataset demonstrates 
              context-dependent model selection. The accompanying R script is fully annotated with <code className="bg-white/20 px-2 py-1 rounded">set.seed(2026)</code> 
              ensuring reproducibility.
            </p>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                Portfolio Project
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                R Statistical Analysis
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                April 2026
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                Version 2.0
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Does Your Clinic Know Where Its Cycles Are Being Lost?
            </h3>
            <p className="text-gray-700 mb-4 max-w-2xl mx-auto">
              If you have cycle-level data and a cancellation rate that is not broken down by stage, patient profile, or protocol. you have the same blind spot this clinic had.
            </p>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              We start with a free 30-minute consultation: we look at what data you have, identify where the gaps are, and show you what is possible with what already exists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8"
              >
                Book a Free Consultation
              </Button>
              <Button 
                onClick={() => navigate('/services')}
                size="lg"
                variant="outline"
                className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 px-8"
              >
                View Our IVF Analytics Services
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default IVFCancellationRiskCaseStudy;
